import type { paperStrategy } from '@whaletail/signal-engine';
import { formatPercent, formatUsd } from '../../lib/format';
import { SHADOW_BUST_NOTE, shadowRunsLine } from './shadowRuns';
import styles from './Btc15ShadowExitPolicyComparison.module.css';

const POLICY_LABEL: Record<string, string> = {
  'hold-to-expiry': 'Hold to expiry (baseline)',
  'take-profit-tight': 'Take-profit (tight)',
  'take-profit-default': 'Take-profit (default — live)',
  'take-profit-loose': 'Take-profit (loose)',
};

function labelFor(policyId: string): string {
  return POLICY_LABEL[policyId] ?? policyId;
}

function teaser(eligibleRounds: number): string {
  return eligibleRounds > 0 ? `${eligibleRounds} rounds replayed` : '—';
}

function dollarPnlFor(
  dollarPnl: paperStrategy.ShadowPolicyDollarPnlSummary[],
  exitPolicyId: string,
): paperStrategy.ShadowPolicyDollarPnlSummary | undefined {
  return dollarPnl.find((entry) => entry.identity.exitPolicyId === exitPolicyId);
}

/**
 * Retrospective-only: replays several exit-policy variants (hold-to-expiry
 * plus tighter/looser take-profit targets) against the same real settled
 * "V1 + take-profit" rounds, using whatever Kalshi quote path the archive
 * actually recorded for each one — both per-$1-contract stats (`summaries`)
 * and real quarter-Kelly dollar P&L (`dollarPnl`, one independent
 * compounding bankroll per policy). This never changes which policy is
 * actually live — btc15-takeprofit-scheduled.ts and its config are
 * completely unaffected by anything shown here. Collapsed by default, same
 * pattern as Btc15TrackRecord/Btc15Diagnostics.
 */
export function Btc15ShadowExitPolicyComparison({
  eligibleRounds,
  summaries,
  dollarPnl,
}: {
  eligibleRounds: number;
  summaries: paperStrategy.ShadowPolicySummary[];
  dollarPnl: paperStrategy.ShadowPolicyDollarPnlSummary[];
}) {
  return (
    <details className={styles.details}>
      <summary className={styles.summary}>
        <span>Shadowed exit-policy comparison (retrospective)</span>
        <span className={styles.teaser}>{teaser(eligibleRounds)}</span>
      </summary>
      <div className={styles.body}>
        <p className={styles.note}>
          Replays each policy against the same settled rounds and archived Kalshi quotes —
          purely retrospective, and never changes which policy is actually live. Every
          figure is after Kalshi&apos;s taker fee on the buy and on any early sale, and
          rounds the live strategy sold early are scored by how they actually settled.
          Rounds settled before the quote archive existed fall back to their real
          settlement for every policy, so this becomes more informative as more history
          accumulates. {SHADOW_BUST_NOTE}
        </p>
        {summaries.length === 0 || eligibleRounds === 0 ? (
          <p className={styles.placeholder}>No settled, funded rounds to replay yet.</p>
        ) : (
          <div className={styles.table}>
            {summaries.map((summary) => {
              const pnl = dollarPnlFor(dollarPnl, summary.identity.exitPolicyId);
              return (
                <div className={styles.row} key={summary.identity.exitPolicyId}>
                  <span className={styles.policyLabel}>
                    {labelFor(summary.identity.exitPolicyId)}
                  </span>
                  <span className={styles.stat}>{summary.rounds} rounds</span>
                  <span className={styles.stat}>{summary.earlyExits} early exits</span>
                  <span className={styles.stat}>
                    {formatUsd(summary.meanPayoffPerContract)}/contract
                  </span>
                  <span className={styles.stat}>
                    {formatPercent(summary.winRate, 0)} win rate
                  </span>
                  {pnl && (
                    <span
                      className={`${styles.stat} ${pnl.pnl >= 0 ? styles.pnlPositive : styles.pnlNegative}`}
                    >
                      {formatUsd(pnl.pnl)} P&amp;L after fees ({formatPercent(pnl.roi, 0)}{' '}
                      ROI, quarter-Kelly)
                    </span>
                  )}
                  {pnl && <span className={styles.stat}>{shadowRunsLine(pnl)}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </details>
  );
}
